import {useContext, createContext, ReactNode, useState} from "react";
import {ChangePasswordRequest, LoginRequest, LoginResponse, User} from "../types";
import {useNavigate} from "react-router-dom";
import {BE_BASE_URL} from "../utils/config";
import {buildPath} from "../utils";

interface Props {
  children: ReactNode;
}

type AuthContextType = {
  user: User | null,
  token: string,
  loginAction: (data: LoginRequest) => Promise<string>
  changePasswordAction: (data: ChangePasswordRequest) => Promise<string>
  logOut: () => void
}

const AuthContext = createContext<AuthContextType | null>(null);

const AuthProvider = ({ children }: Props) => {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [token, setToken] = useState<string>(localStorage.getItem("token") || "");
  const navigate = useNavigate();

  const loginAction = async (data: LoginRequest) => {
    const endpoint = data.admin ? "/admin/login" : "/auth/login";
    try {
      const response = await fetch(BE_BASE_URL + endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      const status = response.status;
      if (status === 400) {
        return 'Invalid Account Type';
      }
      if (status !== 401 && status !== 403 && status !== 200) {
        return 'Server Error';
      }
      const res: LoginResponse = await response.json();
      if (res) {
        const user: User = {username: res.username, role: res.role, sezione_avis: res.sezione_avis};
        setUser(user);
        setToken(res.jwtAuthResponse.accessToken);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("token", res.jwtAuthResponse.accessToken);
        return 'Ok';
      } else {
        return 'Ko';
      }
    } catch (err: unknown) {
      const errMsg = err as Error;
      return errMsg.message;
    }
  };

  const changePasswordAction = async (data: ChangePasswordRequest) => {
    const endpoint = "/auth/change-password";
    try {
      const response = await fetch(BE_BASE_URL + endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      const status = response.status;
      if (status === 400) {
        return 'Invalid Account Type';
      }
      if (status !== 401 && status !== 403 && status !== 200) {
        return 'Server Error';
      }
      return 'Ok'
    } catch (err: unknown) {
      const errMsg = err as Error;
      return errMsg.message;
    }
  };

  const logOut = () => {
    setUser(null);
    setToken("");
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    navigate(buildPath("/"));
  };


  return (
    <AuthContext.Provider value={{ token, user, loginAction, changePasswordAction, logOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
